import {compact, unionBy} from 'lodash';
import React, {Dispatch} from 'react';
import styled from 'styled-components';

import {TagBehaviorsEnum} from '../../../../libs/shared/core/src/models/api/commonTagModel';
import {CompanyModel} from '../../../../libs/shared/core/src/models/api/company/companyModel';
import {globalNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {fetchApiAsync} from '../../../../libs/shared/core/src/services/http/http';
import {ResourceMap} from '../../../../libs/shared/core/src/store/commonAppTypes';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {ellipsis, flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {buildTagSearchUrl, findParentTags, SearchTagStatusesEnum} from '../../../helpers/models/tagHelpers';
import {ApiSearchTags, SearchTagModel} from '../../../models/api/searchTagModel';
import {ApiTag, TagModel} from '../../../models/api/tagModel';
import {AppAction} from '../../../store/appActions';
import {dataUpdatedAction} from '../../../store/data/dataGroupsActions';
import {DropdownItemTagIcon} from '../../common/dropdowns/dropdownItemTagIcon';
import {DropdownFetcher} from '../../common/dropdowns/source/asyncDropdownSource';
import {
  maybeRenderDeduplicationIcon,
  renderFullTagName,
  renderTagNameInTooltip,
} from '../../common/dropdowns/tagDropdownItem';

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
`;

export const StyledTagWrapper = styled.div`
  ${flexCenter};
`;

export const StyledTagTitle = styled.div`
  ${ellipsis()};
`;

export const StyledTagIcon = styled.div`
  margin-right: 4px;
`;
export interface TagsMap {
  [key: number]: TagModel;
}

export function fetchMissingTags(
  selectedIds: ReadonlyArray<number>,
  deletedById: ResourceMap<any>,
  company: CompanyModel,
  tags: ResourceMap<any>,
  dispatch: Dispatch<AppAction>,
  setTagsMap: (map: TagsMap) => void,
) {
  const selectedTagsMap: TagsMap = selectedIds.reduce((prev, tagId) => ({...prev, [tagId]: tags[tagId]}), {});

  const fetchMissingTagsById = async () => {
    const fetchPromises = selectedIds
      .filter((id) => !deletedById[id])
      .map(async (id) => {
        if (selectedTagsMap[id]) {
          return null;
        }
        const url = `${company.url}/tags/${id}`;
        return fetchApiAsync(ApiTag, url);
      });

    const missingTags = compact(await Promise.all(fetchPromises));
    missingTags.forEach((tag) => {
      selectedTagsMap[tag.id] = tag;
    });

    // Add any missing tags into the state
    if (missingTags.length > 0) {
      dispatch(dataUpdatedAction({tags: missingTags}));
    }
    setTagsMap(selectedTagsMap);
  };

  fetchMissingTagsById();
}

export function mapIdsToUndeletedTags(
  selectedIds: ReadonlyArray<number>,
  deletedById: ResourceMap<any>,
  tagsMap: TagsMap,
  tags: ResourceMap<any>,
) {
  return selectedIds
    .filter((id) => !deletedById[id])
    .map((id) => {
      const tag = tagsMap[id] ?? tags[id];

      return [tag, ...findParentTags(tags, tag)];
    });
}

export function buildRowData(
  searchTag: SearchTagModel,
  dropdownSelectedIds: ReadonlyArray<number>,
  makeOnItemSelect: (id: number) => () => void,
  isSearching: boolean,
  initialSelectedIds: ReadonlyArray<number>,
  setTagsMap: (map: TagsMap) => void,
  tagsMap: TagsMap,
  dispatch: Dispatch<AppAction>,
  idsToDisable: ReadonlyArray<number>,
) {
  // Tag search returns an array of tags. The array represents the tag and its parent tags,
  // with the child tag coming first in the array. The request returns every tag, regardless
  // of its place in the hierarchy.
  const currentTag = searchTag[0];
  const isChecked = dropdownSelectedIds.includes(currentTag.id);
  const isAtTop = initialSelectedIds.includes(currentTag.id);
  const isParentAtTop = searchTag[1] && initialSelectedIds.includes(searchTag[1].id);

  const onItemSelect = makeOnItemSelect(currentTag.id);

  const onSelect = () => {
    const newTagsMap = {
      ...tagsMap,
      [currentTag.id]: currentTag,
    };
    setTagsMap(newTagsMap);
    onItemSelect();
    // Add all tags w/parents to the state so we can display it later
    dispatch(dataUpdatedAction({tags: searchTag}));
  };

  const filteredSearchTag = buildFilteredSearchTag(searchTag, isAtTop, initialSelectedIds);
  const shouldCollapse = isSearching || isAtTop || isParentAtTop;

  const nestingLevel =
    filteredSearchTag.length === 1 && shouldCollapse ? 0 : searchTag.length - filteredSearchTag.length;
  // If we are searching or the tag is at the top, we display the tag + it's path on the same line.
  // Otherwise, we display a tree nested view, children tags will also render any parents at the top on the same line
  const tagsPath = shouldCollapse ? filteredSearchTag : [];
  const renderTag = () => (
    <>
      <DropdownItemTagIcon tag={currentTag} nestingLevel={nestingLevel} />
      <DropdownItemContent isChecked={isChecked}>
        <StyledContentDiv>
          {renderFullTagName(currentTag, tagsPath, false)}
          {maybeRenderDeduplicationIcon(currentTag)}
        </StyledContentDiv>
      </DropdownItemContent>
    </>
  );

  const isDisabled = idsToDisable?.includes(currentTag.id);

  return {
    renderer: renderTag,
    onSelect,
    isSelected: isChecked,
    renderTooltip: () => renderTagNameInTooltip(currentTag, tagsPath),
    id: currentTag.id,
    isDisabled,
  };
}

export function buildFilteredSearchTag(
  searchTag: SearchTagModel,
  isAtTop: boolean,
  initialSelectedIds: ReadonlyArray<number>,
) {
  // Return the full path if the tag is at the top
  if (isAtTop) {
    return searchTag;
  }

  let currentIndex = 0;
  // Goes through the array and filter out any parents/grandparents that aren't at the top
  return searchTag.filter((tag, index) => {
    if (index === 0 || (currentIndex === index && initialSelectedIds.includes(tag.id))) {
      currentIndex++;
      return true;
    }
    return false;
  });
}

export function buildFetcher(
  company: CompanyModel,
  namespace: string | undefined,
  initialSelectedItems: ReadonlyArray<SearchTagModel>,
  inboxIds: ReadonlyArray<number>,
): DropdownFetcher<SearchTagModel> {
  return async (query, tags, cancelToken) => {
    if (!namespace || inboxIds.length < 1) {
      return {
        items: [],
        canLoadMore: false,
      };
    }

    // Exclude initial items to compute the offset.
    const existingCount = tags.filter((tag) => tag).length;

    // Saved views can only be created in a specific team identified by its namespace.
    // Also include company tags.
    const namespaces = [globalNamespace, namespace];

    const url = buildTagSearchUrl(company, query, {
      namespaces,
      inboxIds,
      offset: existingCount,
      behaviors: [TagBehaviorsEnum.CAN_DISPLAY],
      status: SearchTagStatusesEnum.ACTIVE,
      source: 'lens-builder-tags',
    });

    const searchResults = await fetchApiAsync(ApiSearchTags, url, {cancelToken});

    return {
      items: unionBy(initialSelectedItems, tags, searchResults.tags, '0.id'),
      canLoadMore: searchResults.tags.length === searchResults.limit,
    };
  };
}
